import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Printer } from 'lucide-react'
import apiClient from '@/api/client'
import { tableQrUrl } from '@/lib/tableQrUrl'
import { invalidateTables } from '@/lib/tablesCache'
import { openPosterSheetPrintWindow } from '@/lib/posterExport'
import { PosterCanvas } from '@/components/admin/marketing/PosterCanvas'
import {
  buildTableTentConfig,
  useTableTentBrand,
  useTableTentDefaults,
} from './tableTentConfig'
import { Button } from '@/components/ui/button'
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import type { DiningTable, PosterConfig } from '@/types'

// Real size is 148 x 210mm. 0.42 puts the preview at roughly 235 x 333 px,
// which fits a dialog without the type becoming unreadable.
const PREVIEW_SCALE = 0.42

/**
 * Print a dine-in QR tent for every table in one pass (F-FE-N100).
 *
 * The per-table sheet covers replacing one damaged card. This covers the other
 * case, which is the one that actually happens: a restaurant switching dine-in
 * QR on for the first time has forty tables and wants one print job, not forty
 * downloads and forty trips to the printer.
 *
 * Reuses the poster studio's A5 tent layout rather than inventing a print
 * template - same canvas, same themes, same export path, so what prints here
 * matches what the studio previews.
 */
export function TableTentSheet({
  tables,
  onClose,
}: {
  tables: DiningTable[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [printing, setPrinting] = useState(false)

  // The origin the printed codes point at. Read from the resolver, not from a
  // settings row - same reason as TableQrSheet: the public base usually comes
  // from MARKETING_QR_REDIRECT_BASE or auto-detect, and is not in app_settings.
  const publicLinks = useQuery({
    queryKey: ['public-links'],
    queryFn: () => apiClient.getPublicLinks(),
    staleTime: 60_000,
  })
  const publicBase = publicLinks.data?.data?.resolved?.url ?? ''
  const scannable = publicLinks.data?.data?.resolved?.is_scannable !== false
  // The storefront origin the /q/t/<slug> redirect hands the diner to. Distinct
  // from publicBase above, which is the POS host that merely serves /q/. Unset
  // means a scanned code lands nowhere, so it must block printing.
  const orderingBase = publicLinks.data?.data?.ordering?.url ?? ''

  const { data: settings } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const allSettings = settings?.data as Record<string, unknown> | undefined
  // Both flags, not just the dine-in one — orders are accepted on either, so
  // keying on dine-in alone warned every tenant with the full add-on that their
  // codes would not work, while they did.
  const capabilityOff =
    allSettings != null &&
    allSettings.feature_dine_in_qr === false &&
    allSettings.feature_online_ordering === false

  // The tenant's own brand block, shared with the single-table reprint.
  const brand = useTableTentBrand()

  // Mint a slug for every table that has none. Run as a query rather than a
  // mutation because EnsureSlug is idempotent by design - a table that already
  // has a code gets it back untouched - so a refetch cannot invalidate a card
  // already laminated to a table. Sequential: forty simultaneous POSTs is a
  // burst the shared rate limiter has no reason to tolerate.
  const tableIds = useMemo(() => tables.map((t) => t.id).join(','), [tables])
  const slugsQuery = useQuery({
    queryKey: ['table-tent-slugs', tableIds],
    queryFn: async () => {
      const slugs: Record<string, string> = {}
      let minted = 0
      let failed = 0
      for (const table of tables) {
        const existing = (table.qr_slug ?? '').trim()
        if (existing) {
          slugs[table.id] = existing
          continue
        }
        try {
          const res = await apiClient.ensureTableQr(table.id)
          const slug = (res?.data?.qr_slug ?? '').trim()
          if (slug) {
            slugs[table.id] = slug
            minted++
          } else {
            failed++
          }
        } catch {
          // One unreachable table must not cost the operator the other 39
          // tents. It is reported below and simply omitted from the sheet.
          failed++
        }
      }
      return { slugs, minted, failed }
    },
    staleTime: 1000 * 60,
    retry: false,
  })

  // Newly minted codes change what the table list should show. Fires once -
  // invalidateTables refetches ['tables-summary'], which is the prop feeding
  // this component, and re-running the mint on that refetch would loop.
  const announced = useRef(false)
  useEffect(() => {
    if (!announced.current && (slugsQuery.data?.minted ?? 0) > 0) {
      announced.current = true
      invalidateTables(qc)
    }
  }, [slugsQuery.data?.minted, qc])

  const tentDefaults = useTableTentDefaults()

  const posters = useMemo(() => {
    const slugs = slugsQuery.data?.slugs ?? {}
    const out: Array<{ table: DiningTable; config: PosterConfig }> = []
    for (const table of tables) {
      const url = tableQrUrl(publicBase, slugs[table.id] ?? table.qr_slug ?? '')
      if (!url) continue
      // Shared with the single-table reprint in TableQrSheet, so a replacement
      // tent for table 7 is identical to the one this run produced.
      out.push({ table, config: buildTableTentConfig(tentDefaults, table.table_number, url) })
    }
    return out
  }, [tables, publicBase, slugsQuery.data, tentDefaults])

  const preparing = slugsQuery.isPending || publicLinks.isLoading
  const failed = slugsQuery.data?.failed ?? 0
  const preview = posters[0]

  const handlePrint = async () => {
    if (posters.length === 0) return
    setPrinting(true)
    try {
      await openPosterSheetPrintWindow({
        posters: posters.map((p) => ({ config: p.config })),
        brand,
        purpose: 'dine_in',
        documentTitle: `Table QR tents - ${brand.businessName || 'restaurant'}`,
      })
    } catch (error) {
      toastHelpers.apiError('Print table tents', error)
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" /> Print table QRs
        </DialogTitle>
        <DialogDescription>
          One A5 tent per table, one page each. Tables without a code get one generated now;
          codes already in use are left alone.
        </DialogDescription>
      </DialogHeader>

      {capabilityOff && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
          These codes will not accept orders until dine-in QR ordering is enabled for this
          restaurant. You can still print them.
        </p>
      )}

      {!orderingBase && !publicLinks.isLoading && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          No customer ordering address is configured, so scanning this code lands on the staff
          login instead of your menu. Set the storefront URL before printing.
        </p>
      )}

      {!publicBase && !publicLinks.isLoading && (
        <p className="text-sm text-destructive">
          No public address is configured, so a printed code could not be scanned. Set it in
          Settings before printing.
        </p>
      )}

      {publicBase && !scannable && (
        <p className="text-sm text-destructive">
          The public address for this restaurant ({publicBase}) is a local one, so these codes
          will not open from a customer phone. Fix it in Settings before printing.
        </p>
      )}

      {slugsQuery.isError && (
        <p className="text-sm text-destructive">
          Could not generate codes for these tables. Try again, or reload the page.
        </p>
      )}

      {failed > 0 && (
        <p className="text-sm text-destructive">
          {failed} table(s) could not be given a code and are not in this sheet. Retry, or open
          those tables individually.
        </p>
      )}

      <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-border bg-muted/40 p-4">
        {preparing ? (
          <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            Preparing codes for {tables.length} table(s)...
          </div>
        ) : preview ? (
          <PosterCanvas
            config={preview.config}
            brand={brand}
            purpose="dine_in"
            scale={PREVIEW_SCALE}
            forPrint
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing to print yet - no table has a scannable code.
          </p>
        )}
      </div>

      {preview && !preparing && (
        <p className="text-center text-xs text-muted-foreground">
          Preview of Table {preview.table.table_number}. Printing sends {posters.length} page
          {posters.length === 1 ? '' : 's'} - one tent per table, in table order.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          onClick={handlePrint}
          disabled={preparing || printing || posters.length === 0}
          className="gap-2"
        >
          {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Print {posters.length || ''} tent{posters.length === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  )
}
